import { useState } from 'react';
import type { AttemptCardContent } from './AttemptCard';

// ACC.A2 (#412) — Dev Agent self-review card. Mounts in the session
// view alongside the ImplementationPromptCard, rendering the
// dev_reflection JSONB content as a collapsible orientation surface.
//
// Read-only — agent-generated, no editing path. The reflection is
// produced by the 3-attempt self-correction loop in
// dev-reflection.service.ts and persisted as a dev_reflection artefact;
// this card just renders the JSONB.
//
// ACC.B5 (#419) — extended with a three-count summary (issues found /
// resolved / requiring human attention) derived from the per-attempt
// records persisted in agent_messages. The card also shows the
// "Self-review passed — no shortcuts identified" empty state when no
// self-correction attempts ran AND the assessment is READY_FOR_REVIEW.

export interface DevReflectionCardContent {
  implementationArtefactId: string;
  selfReviewedAt: string;
  acsCovered: string[];
  shortcutsIdentified: string[];
  scopeAdditions: string[];
  selfAssessment: 'READY_FOR_REVIEW' | 'NEEDS_REWORK';
  selfCorrectionAttempts: number;
}

interface DevReflectionCardProps {
  artefact: {
    id: string;
    title: string;
    content: DevReflectionCardContent;
  };
  /**
   * ACC.B5 (#419) — chronological per-attempt records persisted in
   * agent_messages. Used to derive the three-count summary
   * (found / resolved / requiring human attention). When omitted (e.g.
   * a legacy reflection persisted before B5), the summary falls back to
   * counts derived solely from selfCorrectionAttempts and selfAssessment.
   */
  attempts?: AttemptCardContent[];
}

interface SummaryCounts {
  found:                    number;
  resolved:                 number;
  requiringHumanAttention:  number;
}

function deriveSummaryCounts(
  attempts: AttemptCardContent[] | undefined,
  content: DevReflectionCardContent,
): SummaryCounts {
  if (attempts && attempts.length > 0) {
    return {
      found:                   attempts.length,
      resolved:                attempts.filter((a) => a.result === 'resolved').length,
      requiringHumanAttention: attempts.filter((a) => a.result === 'escalated').length,
    };
  }
  // Legacy fallback: no per-attempt records (pre-B5 reflection on session
  // restore, OR a reflection that converged on the initial review with
  // selfCorrectionAttempts=0). Derive a conservative summary from the
  // final-state JSON.
  //
  // Round-1 review LOW: a NEEDS_REWORK final after a 3-attempt cap-out
  // means the loop never converged. Without per-attempt rows we can't
  // tell which intermediate attempts the regen addressed; the safer
  // default is to surface ALL attempts as requiring human attention
  // (rather than 1) so the operator-facing count doesn't understate
  // the work that's still pending.
  const isReady = content.selfAssessment === 'READY_FOR_REVIEW';
  return {
    found:                   content.selfCorrectionAttempts,
    resolved:                isReady ? content.selfCorrectionAttempts : 0,
    requiringHumanAttention: isReady ? 0 : content.selfCorrectionAttempts,
  };
}

export default function DevReflectionCard({ artefact, attempts }: DevReflectionCardProps) {
  const [expanded, setExpanded] = useState(false);
  const r = artefact.content;
  const isReady = r.selfAssessment === 'READY_FOR_REVIEW';
  const summary = deriveSummaryCounts(attempts, r);
  const showEmptyState = isReady
    && r.selfCorrectionAttempts === 0
    && r.shortcutsIdentified.length === 0
    && r.scopeAdditions.length === 0;

  // Headline badge styling: green for READY_FOR_REVIEW, amber for
  // NEEDS_REWORK. Assessment colors match the existing ResumeBanner /
  // TierUpgradeBanner conventions in the codebase.
  const badgeColors = isReady
    ? { bg: 'rgba(34, 197, 94, 0.10)', border: 'rgba(34, 197, 94, 0.35)', text: 'rgb(22, 163, 74)' }
    : { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.45)', text: 'rgb(180, 83, 9)' };

  return (
    <div
      data-testid="dev-reflection-card"
      style={{
        borderLeft: '3px solid var(--primary)',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeftColor: isReady ? 'rgb(34, 197, 94)' : 'rgb(245, 158, 11)',
        borderLeftWidth: 3,
        padding: 16,
        marginTop: 8,
        marginBottom: 8,
      }}
    >
      {/* Header — title + assessment badge + collapse toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: expanded ? 12 : 0 }}>
        {isReady
          ? <CheckCircleIcon />
          : <AlertTriangleIcon />
        }
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Dev Agent Self-Review
          </div>
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--text)',
              marginTop: 2,
            }}
          >
            {artefact.title}
          </div>
        </div>
        <span
          data-testid="dev-reflection-assessment-badge"
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            fontWeight: 600,
            padding: '3px 8px',
            borderRadius: 12,
            background: badgeColors.bg,
            border: `1px solid ${badgeColors.border}`,
            color: badgeColors.text,
            whiteSpace: 'nowrap',
          }}
        >
          {isReady ? 'Ready for review' : 'Needs rework'}
        </span>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={`dev-reflection-body-${artefact.id}`}
          data-testid="dev-reflection-toggle"
          style={{
            background: 'none',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            padding: '4px 10px',
            fontSize: 12,
            fontFamily: 'var(--font-body)',
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          {expanded ? 'Hide' : 'Show'}
        </button>
      </div>

      {/* ACC.B5 (#419) — empty state when the self-review passed without
          ever needing a regen. Renders the AC-mandated copy. */}
      {showEmptyState && (
        <div
          data-testid="dev-reflection-empty-state"
          style={{
            fontFamily:   'var(--font-body)',
            fontSize:     13,
            color:        'var(--text-secondary)',
            marginBottom: expanded ? 8 : 0,
          }}
        >
          Self-review passed — no shortcuts identified.
        </div>
      )}

      {/* Self-correction attempts counter — subtle; only shown when > 0 */}
      {r.selfCorrectionAttempts > 0 && (
        <div
          data-testid="dev-reflection-attempts"
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 11,
            color: 'var(--text-secondary)',
            marginBottom: expanded ? 8 : 0,
          }}
        >
          Self-corrected {r.selfCorrectionAttempts === 1 ? 'once' : `${r.selfCorrectionAttempts} times`} before this assessment.
        </div>
      )}

      {/* ACC.B5 (#419) — three-count summary surfaced whenever any
          self-correction work happened (even when it converged cleanly).
          Hidden in the empty-state branch — there is nothing to count. */}
      {!showEmptyState && summary.found > 0 && (
        <div
          data-testid="dev-reflection-summary"
          style={{
            display:      'flex',
            gap:          12,
            flexWrap:     'wrap',
            marginTop:    6,
            marginBottom: expanded ? 8 : 0,
            fontFamily:   'var(--font-body)',
            fontSize:     12,
          }}
        >
          <SummaryStat label="Issues found"       value={summary.found}                   testId="dev-reflection-summary-found" />
          <SummaryStat label="Resolved"           value={summary.resolved}                testId="dev-reflection-summary-resolved" tone="success" />
          <SummaryStat label="Need human attention" value={summary.requiringHumanAttention} testId="dev-reflection-summary-requiring" tone={summary.requiringHumanAttention > 0 ? 'warning' : 'neutral'} />
        </div>
      )}

      {/* Body — collapsible. Three sections: ACs, shortcuts, scope additions. */}
      {expanded && (
        <div id={`dev-reflection-body-${artefact.id}`} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Section
            heading="Acceptance criteria covered"
            items={r.acsCovered}
            emptyMessage="No acceptance criteria explicitly listed in the self-review."
            tone="neutral"
            testId="dev-reflection-acs"
          />
          {r.shortcutsIdentified.length > 0 && (
            <Section
              heading="Shortcuts identified"
              items={r.shortcutsIdentified}
              emptyMessage=""
              tone="warning"
              testId="dev-reflection-shortcuts"
            />
          )}
          {r.scopeAdditions.length > 0 && (
            <Section
              heading="Scope additions"
              items={r.scopeAdditions}
              emptyMessage=""
              tone="info"
              testId="dev-reflection-scope-additions"
            />
          )}
        </div>
      )}
    </div>
  );
}

// ACC.B5 (#419) — single chip in the three-count summary row. Tone
// drives the chip's accent: 'success' for the resolved count, 'warning'
// when the requiring-human-attention count is non-zero, 'neutral' for
// the totals row.
function SummaryStat({
  label,
  value,
  testId,
  tone = 'neutral',
}: {
  label:   string;
  value:   number;
  testId?: string;
  tone?:   'neutral' | 'success' | 'warning';
}) {
  const colors =
    tone === 'success' ? { bg: 'rgba(34, 197, 94, 0.10)',  border: 'rgba(34, 197, 94, 0.35)',  text: 'rgb(22, 163, 74)' } :
    tone === 'warning' ? { bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.45)', text: 'rgb(180, 83, 9)' } :
                         { bg: 'var(--surface-alt, rgba(0,0,0,0.04))', border: 'var(--border)', text: 'var(--text-secondary)' };
  return (
    <div
      data-testid={testId}
      style={{
        display:      'inline-flex',
        alignItems:   'center',
        gap:          6,
        padding:      '3px 10px',
        borderRadius: 12,
        background:   colors.bg,
        border:       `1px solid ${colors.border}`,
        color:        colors.text,
      }}
    >
      <span style={{ fontWeight: 600 }} data-testid={testId ? `${testId}-value` : undefined}>
        {value}
      </span>
      <span>{label}</span>
    </div>
  );
}

interface SectionProps {
  heading: string;
  items: string[];
  emptyMessage: string;
  tone: 'neutral' | 'warning' | 'info';
  testId?: string;
}

function Section({ heading, items, emptyMessage, tone, testId }: SectionProps) {
  const dotColor =
    tone === 'warning' ? 'rgb(245, 158, 11)' :
    tone === 'info'    ? 'rgb(59, 130, 246)' :
                         'rgb(34, 197, 94)';
  return (
    <div data-testid={testId}>
      <div
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--text)',
          marginBottom: 6,
        }}
      >
        {heading}
      </div>
      {items.length === 0 ? (
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          {emptyMessage}
        </div>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {items.map((item, i) => (
            <li
              key={i}
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 13,
                color: 'var(--text)',
                paddingLeft: 14,
                position: 'relative',
                lineHeight: 1.5,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 2,
                  top: 7,
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: dotColor,
                }}
              />
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// SVG icons co-located so the component has no icon-library dependency.
function CheckCircleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(34, 197, 94)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}

function AlertTriangleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="rgb(245, 158, 11)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}
